<template>
  <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
    <button
      ref="button"
      type="button"
      :disabled="sending || secondsLeft > 0"
      class="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white/10 dark:text-white dark:inset-ring-white/10 dark:hover:bg-white/20 cursor-pointer"
      @click="resend"
    >
      <EnvelopeIcon class="-ml-0.5 size-4 text-gray-400" aria-hidden="true" />
      <template v-if="sending">Sending…</template>
      <template v-else-if="secondsLeft > 0">Resend in {{ secondsLeft }}s</template>
      <template v-else>Send a new code</template>
    </button>
    <p
      v-if="message"
      class="text-sm"
      :class="
        messageIsError ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'
      "
      role="status"
    >
      {{ message }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { EnvelopeIcon } from '@heroicons/vue/24/outline';
import { useAuthStore } from '@/stores/auth';
import { problemCode } from '@/lib/email-verification';

const auth = useAuthStore();
const emit = defineEmits<{ sent: []; alreadyVerified: [] }>();
const button = ref<HTMLButtonElement | null>(null);
defineExpose({ focus: () => button.value?.focus() });

const sending = ref(false);
const message = ref<string | null>(null);
const messageIsError = ref(false);
const nowMs = ref(Date.now());
let timer: ReturnType<typeof setInterval> | null = null;

const secondsLeft = computed(() => {
  const until = auth.resendAvailableAtMs;
  return until ? Math.max(0, Math.ceil((until - nowMs.value) / 1000)) : 0;
});

function stopTimer() {
  if (timer) clearInterval(timer);
  timer = null;
}

watch(
  () => auth.resendAvailableAtMs,
  (until) => {
    nowMs.value = Date.now();
    stopTimer();
    if (until && until > nowMs.value) {
      timer = setInterval(() => {
        nowMs.value = Date.now();
        if (secondsLeft.value === 0) stopTimer();
      }, 1000);
    }
  },
  { immediate: true }
);

async function resend() {
  sending.value = true;
  message.value = null;
  messageIsError.value = false;
  try {
    const result = await auth.resendVerification();
    if (!result) return;
    if (result.status === 'sent') {
      message.value = `A new code was sent to ${auth.username ?? 'your email address'}.`;
      emit('sent');
    } else if (result.status === 'already_verified') {
      message.value = 'Your email address is already verified.';
      emit('alreadyVerified');
    } else {
      message.value = 'Too many requests. Please wait before trying again.';
      messageIsError.value = true;
    }
  } catch (e: unknown) {
    const response = (e as { response?: unknown }).response;
    if (problemCode(e) === 'EMAIL_SEND_FAILED') {
      message.value = 'The verification email could not be sent. Please try again later.';
    } else {
      message.value = response
        ? 'Something went wrong. Please try again later.'
        : 'Unable to connect to the server. Please check your network.';
    }
    messageIsError.value = true;
  } finally {
    sending.value = false;
  }
}

onBeforeUnmount(stopTimer);
</script>
